/**
 * WHICH PHOTOS A PRODUCT PAGE SHOWS.
 *
 * Migration review, blocker 1.7 — "on their most-considered products, we show
 * one photo where they show 42" — and its root cause, "product photos live in
 * two tables and three screens disagree about which."
 *
 * There are two image tables. `product_image` holds the product's gallery: the
 * angles, the packaging shot, the model shot, the size chart. `variant_image`
 * holds the ONE picture that identifies a variation, which is what a swatch
 * shows. A catalogue photographed in our portal fills both; a catalogue
 * imported from WooCommerce fills only the first — dk-store has 2,120 product
 * images and, per variation, exactly one.
 *
 * The product page read `selectedVariant.images` and nothing else. On every
 * catalogue whose photos live on the product that is a gallery of one, and
 * measured across dk-store it put 562 of 2,116 photographs (26.6%) out of reach
 * of every page in the storefront. The shop grid was reading the other table
 * all along, which is why this was invisible until a real merchant's data
 * arrived: a normally-seeded demo salon fills both tables and the two reads
 * agree.
 *
 * ── THE RULE ─────────────────────────────────────────────────────────────
 *
 *   the chosen variation's photos, then the product's own, deduplicated by URL.
 *
 * Each half is load-bearing:
 *
 *  • THE VARIANT LEADS, so the gallery agrees with the swatch the shopper just
 *    pressed. Showing the product's primary shot after a tap on "ורוד בהיר" is
 *    the old bug the other way up — a page that will not show you what you
 *    chose.
 *  • THE PRODUCT FOLLOWS, so the second angle that decides a jewellery-set
 *    purchase is reachable at all.
 *  • DEDUPLICATED, because the importer writes the variation's shot into both
 *    tables: without this, every dk-store gallery would open on the same
 *    picture twice.
 *
 * The rule is here, and not inline in `Pages/ProductDetail/index.js`, because
 * three surfaces answer the same question and the review found them
 * disagreeing. The "you may also like" strip is the third; it is fed by
 * `/products/:id/related`, which serialises no product images at all, and
 * cannot be fixed from the client — see the report.
 */

/** A row that can actually be rendered. */
const usable = (i) => !!(i && typeof i.url === 'string' && i.url.trim());

/**
 * The product's own gallery, in the order the owner arranged it: whatever they
 * marked primary first, then by `position`. The API returns rows in an order
 * nothing promises, and `images[0]` on an unordered list is how a page ends up
 * leading with the size chart.
 */
const orderedProductImages = (product) => (Array.isArray(product?.images) ? product.images : [])
  .filter(usable)
  .map((image, index) => ({ image, index }))
  .sort((a, b) => {
    if (!!b.image.isPrimary !== !!a.image.isPrimary) return b.image.isPrimary ? 1 : -1;
    const pa = a.image.position ?? Number.MAX_SAFE_INTEGER;
    const pb = b.image.position ?? Number.MAX_SAFE_INTEGER;
    // A stable sort by index, so two rows sharing a position keep the order the
    // server sent rather than swapping between engines.
    return pa - pb || a.index - b.index;
  })
  .map((e) => e.image);

/* ── BUT NOT ANOTHER COLOUR'S ─────────────────────────────────────────────
 *
 * Seen live on 2026-09-17 on DK Store's PHLYE ROMA luggage set, which a Meta ad shows with a row
 * of its six colours: a shopper who tapped black landed on black, then brown, light blue and pink.
 * "The product's own" is every colour's photo — a WooCommerce import gives each variation ONE shot
 * out of the product's gallery (stylon-server `catalog-migration/woo-catalog.map.ts`) — so the
 * product's half brought the other five colours straight back in.
 *
 * A photo is a colour's when a variation of that colour carries it, and the product's half now
 * leaves out every photo that is another colour's and not the chosen colour's too. A photo no
 * colour carries still follows: nothing says whose it is.
 *
 * "Colour" is the option a shopper tells the product apart by: the colour slot, or the type slot
 * on a product that has no colours (where name-only variations land) — the axis the full-screen
 * viewer's picker shows. Sizes of one colour are that colour.
 *
 * ONE THING DOES NOT MAKE A PHOTO ANYBODY'S. A variation with no photo of its own, whose colour
 * has none either, is sent the product's WHOLE gallery (stylon-server `resolveVariantImages`, the
 * last step of its fallback). That means "I have nothing of my own", and read as a claim it would
 * hand every photo to that colour and strip the rest of the product bare. */

/** The option a shopper tells this product's variations apart by, or null when there is none. */
function lookAxisOf(variants) {
  if (variants.some((v) => v && v.color)) return 'color';
  if (variants.some((v) => v && v.style)) return 'style';
  return null;
}

const urlsOf = (images) => (Array.isArray(images) ? images : []).filter(usable).map((i) => i.url.trim());

/**
 * The photos `product`'s gallery must leave out when `chosen` (one variation or several) is what
 * the shopper is looking at: those another option's variations carry and the chosen option's do
 * not. Empty when nothing is chosen, or the product has no options that look different.
 */
export function otherOptionsPhotoUrls(product, chosen) {
  const variants = (Array.isArray(product?.variants) ? product.variants : []).filter((v) => v && typeof v === 'object');
  const picked = (Array.isArray(chosen) ? chosen : [chosen]).filter((v) => v && typeof v === 'object');
  const axis = lookAxisOf(variants.length ? variants : picked);
  const out = new Set();
  if (!axis || !picked.length) return out;
  const chosenValues = new Set(picked.map((v) => v[axis]));
  // A pick with no value on the axis says nothing about which photos are its.
  if (chosenValues.has(undefined) || chosenValues.has(null) || chosenValues.has('')) return out;

  const productUrls = new Set(urlsOf(product?.images));
  const isWholeGallery = (urls) => productUrls.size > 0 && [...productUrls].every((url) => urls.includes(url));
  const mine = new Set();
  for (const v of [...picked, ...variants.filter((row) => chosenValues.has(row[axis]))]) {
    urlsOf(v.images).forEach((url) => mine.add(url));
  }
  for (const v of variants) {
    if (!v[axis] || chosenValues.has(v[axis])) continue;
    const urls = urlsOf(v.images);
    if (isWholeGallery(urls)) continue;
    urls.forEach((url) => { if (!mine.has(url)) out.add(url); });
  }
  return out;
}

/**
 * Every photo this page can show for `product` with `selectedVariant` chosen.
 * Never null; safe on a half-loaded payload.
 */
export function galleryImages(product, selectedVariant) {
  const variantImages = (Array.isArray(selectedVariant?.images) ? selectedVariant.images : []).filter(usable);
  const othersPhotos = otherOptionsPhotoUrls(product, selectedVariant);
  const seen = new Set();
  const out = [];
  for (const image of [...variantImages, ...orderedProductImages(product)]) {
    const key = image.url.trim();
    if (seen.has(key) || othersPhotos.has(key)) continue;
    seen.add(key);
    out.push(image);
  }
  return out;
}

/** How many photos the rail will count — the "1 / 42" a shopper reads. */
export function galleryImageCount(product, selectedVariant) {
  return galleryImages(product, selectedVariant).length;
}
